import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Ship, Phone, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Track Shipment', path: '/tracking' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="bg-navy text-white py-2 hidden lg:block">
        <div className="rsl-container flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2 font-mono text-xs tracking-wide">
              <Phone className="w-3 h-3 text-safety-orange" />
              (415) 329-9700
            </span>
            <span className="text-white/60">|</span>
            <span className="text-white/70">967 E 12th St, Los Angeles, CA 90021</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/70 font-mono text-xs">Delivering Excellence Since 1975</span>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav
        data-testid="main-navbar"
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white shadow-lg'
            : location.pathname === '/'
            ? 'bg-transparent'
            : 'bg-white shadow-sm'
        }`}
      >
        <div className="rsl-container">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className={`p-2 ${isScrolled || location.pathname !== '/' ? 'bg-navy' : 'bg-white/10 backdrop-blur'}`}>
                <Ship className={`w-8 h-8 ${isScrolled || location.pathname !== '/' ? 'text-safety-orange' : 'text-white'}`} />
              </div>
              <div>
                <h1 className={`font-black text-xl tracking-tight ${isScrolled || location.pathname !== '/' ? 'text-navy' : 'text-white'}`}>
                  RSL
                </h1>
                <p className={`text-[10px] font-mono tracking-widest uppercase ${isScrolled || location.pathname !== '/' ? 'text-slate-500' : 'text-white/70'}`}>
                  Robert Shipping
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  className={`nav-link ${
                    isScrolled || location.pathname !== '/'
                      ? location.pathname === link.path
                        ? 'text-safety-orange'
                        : 'text-navy hover:text-safety-orange'
                      : location.pathname === link.path
                      ? 'text-safety-orange'
                      : 'text-white hover:text-safety-orange'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <Link to="/quote">
                <Button
                  data-testid="get-quote-btn"
                  className="bg-safety-orange hover:bg-orange-600 text-white font-bold uppercase tracking-widest text-xs px-6 h-12"
                >
                  Get Quote
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className={`w-6 h-6 ${isScrolled || location.pathname !== '/' ? 'text-navy' : 'text-white'}`} />
              ) : (
                <Menu className={`w-6 h-6 ${isScrolled || location.pathname !== '/' ? 'text-navy' : 'text-white'}`} />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu lg:hidden" data-testid="mobile-menu">
          {navLinks.map((link, index) => (
            <Link
              key={link.path}
              to={link.path}
              className="mobile-menu-link"
              style={{ animationDelay: `${index * 0.1}s` }}
              data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
            >
              {link.name}
            </Link>
          ))}
          <Link to="/quote" className="mt-8">
            <Button className="bg-safety-orange hover:bg-orange-600 text-white font-bold uppercase tracking-widest text-sm px-8 h-14">
              Get Quote
            </Button>
          </Link>
        </div>
      )}
    </>
  );
};

export default Navbar;
